import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, Sparkles, Loader2, Trash2, Copy, Download, FileText, Presentation as PresentationIcon,
  Palette, Type, LayoutTemplate, ChevronLeft, ChevronRight,
} from "lucide-react";
import { docsApi, slidesApi } from "../../api";
import { TEMPLATES, FONTS, LAYOUTS } from "./templates";
import SlidePreview from "./SlidePreview";

const KIND = "slides";

const newId = () => `s-${Date.now()}-${Math.floor(Math.random() * 99999)}`;

const emptySlide = (layout = "bullets") => ({
  id: newId(),
  layout,
  heading: "New slide",
  subheading: "",
  bullets: layout === "bullets" ? ["Point 1", "Point 2", "Point 3"] : [],
  stats: layout === "stat" || layout === "infographic" ? [{ label: "Growth", value: "42", unit: "%" }] : [],
  quote: layout === "quote" ? "Ideas are commodity. Execution of them is not." : "",
  author: layout === "quote" ? "Michael Dell" : "",
  columns: layout === "two_column" || layout === "comparison" ? [{ heading: "Left", body: "" }, { heading: "Right", body: "" }] : [],
  chart: layout === "chart" ? { kind: "bar", data: [{ name: "Q1", value: 12 }, { name: "Q2", value: 24 }, { name: "Q3", value: 18 }, { name: "Q4", value: 32 }] } : null,
  timeline: layout === "timeline" ? [{ date: "2024", title: "Launch", desc: "Initial rollout" }] : [],
  notes: "",
  font_override: null,
});

const defaultDeck = () => ({
  title: "Untitled Presentation",
  template: "modern",
  slides: [
    { ...emptySlide("title"), heading: "My Presentation", subheading: "A stunning deck" },
    emptySlide("bullets"),
    { ...emptySlide("closing"), heading: "Thank you", subheading: "Questions?" },
  ],
});

const normalize = (content) => {
  if (!content || typeof content !== "object") return defaultDeck();
  return {
    title: content.title || "Untitled Presentation",
    template: content.template || "modern",
    slides: Array.isArray(content.slides) && content.slides.length ? content.slides : defaultDeck().slides,
  };
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a);
  a.click(); a.remove(); URL.revokeObjectURL(url);
};

export default function Slides() {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null); // { id, title, content: { title, template, slides } }
  const [selIdx, setSelIdx] = useState(0);
  const [aiBusy, setAiBusy] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [exportBusy, setExportBusy] = useState(null); // "pptx" | "pdf" | null
  const [aiTopic, setAiTopic] = useState("");
  const [aiSlideCount, setAiSlideCount] = useState(8);
  const [aiTone, setAiTone] = useState("professional");
  const [aiAudience, setAiAudience] = useState("");
  const [aiCharts, setAiCharts] = useState(true);
  const [aiInfographics, setAiInfographics] = useState(true);
  const stageRef = useRef(null);
  const [stageScale, setStageScale] = useState(0.85);

  const load = async () => {
    const list = await docsApi.list(KIND);
    // Newest first — so the most recently edited deck opens by default
    const sorted = [...list].sort((a, b) => {
      const ta = new Date(a.updated_at || a.created_at || 0).getTime();
      const tb = new Date(b.updated_at || b.created_at || 0).getTime();
      return tb - ta;
    });
    setDocs(sorted);
    if (!active && sorted[0]) {
      setActive({ ...sorted[0], content: normalize(sorted[0].content) });
      setSelIdx(0);
    }
  };
  useEffect(() => { load(); }, []);

  // Auto-save (debounced)
  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => {
      docsApi.update(active.id, { kind: KIND, title: active.title || active.content?.title || "Untitled", content: active.content })
        .then(d => setDocs(ds => ds.map(x => x.id === d.id ? { ...d } : x)))
        .catch(() => {});
    }, 900);
    return () => clearTimeout(t);
  }, [active]);

  // Auto-fit stage scale to available space
  useEffect(() => {
    if (!stageRef.current) return;
    const el = stageRef.current;
    const compute = () => {
      const w = el.clientWidth - 32;
      const h = el.clientHeight - 32;
      const s = Math.min(w / 960, h / 540, 1);
      setStageScale(Math.max(0.35, s));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [active?.id, showTemplates]);

  const theme = useMemo(() => {
    const t = active?.content?.template || "modern";
    return TEMPLATES.find(x => x.id === t) || TEMPLATES[0];
  }, [active?.content?.template]);

  const slide = active?.content?.slides?.[selIdx];

  const patchDeck = (patch) => {
    setActive(a => a ? { ...a, content: { ...a.content, ...patch } } : a);
  };
  const patchSlide = (patch) => {
    setActive(a => {
      if (!a) return a;
      const slides = a.content.slides.map((s, i) => {
        if (i !== selIdx) return s;
        // When switching layout, seed defaults for the target layout if the field is empty
        if (patch.layout && patch.layout !== s.layout) {
          const seed = emptySlide(patch.layout);
          return {
            ...s,
            ...patch,
            bullets: (s.bullets && s.bullets.length) ? s.bullets : seed.bullets,
            stats: (s.stats && s.stats.length) ? s.stats : seed.stats,
            quote: s.quote || seed.quote,
            author: s.author || seed.author,
            columns: (s.columns && s.columns.length) ? s.columns : seed.columns,
            chart: s.chart || seed.chart,
            timeline: (s.timeline && s.timeline.length) ? s.timeline : seed.timeline,
          };
        }
        return { ...s, ...patch };
      });
      return { ...a, content: { ...a.content, slides } };
    });
  };

  const createDeck = async () => {
    const created = await docsApi.create({ kind: KIND, title: "Untitled Presentation", content: defaultDeck() });
    setDocs(ds => [created, ...ds]);
    setActive({ ...created, content: normalize(created.content) });
    setSelIdx(0);
  };

  const deleteDeck = async () => {
    if (!active) return;
    if (!window.confirm("Delete this presentation?")) return;
    await docsApi.remove(active.id);
    setDocs(ds => ds.filter(x => x.id !== active.id));
    setActive(null);
  };

  const addSlide = (layout = "bullets") => {
    setActive(a => {
      if (!a) return a;
      const slides = [...a.content.slides];
      const ins = Math.min(selIdx + 1, slides.length);
      slides.splice(ins, 0, emptySlide(layout));
      return { ...a, content: { ...a.content, slides } };
    });
    setSelIdx(i => i + 1);
  };

  const duplicateSlide = () => {
    setActive(a => {
      if (!a) return a;
      const s = a.content.slides[selIdx]; if (!s) return a;
      const slides = [...a.content.slides];
      slides.splice(selIdx + 1, 0, { ...JSON.parse(JSON.stringify(s)), id: newId() });
      return { ...a, content: { ...a.content, slides } };
    });
    setSelIdx(i => i + 1);
  };

  const removeSlide = () => {
    setActive(a => {
      if (!a) return a;
      if (a.content.slides.length <= 1) return a;
      const slides = a.content.slides.filter((_, i) => i !== selIdx);
      return { ...a, content: { ...a.content, slides } };
    });
    setSelIdx(i => Math.max(0, i - 1));
  };

  const runAI = async () => {
    if (!aiTopic.trim()) return;
    setAiBusy(true);
    try {
      const res = await slidesApi.generate({
        topic: aiTopic.trim(),
        num_slides: aiSlideCount,
        template: active?.content?.template || "modern",
        tone: aiTone,
        audience: aiAudience,
        include_charts: aiCharts,
        include_infographics: aiInfographics,
      });
      if (res?.slides?.length) {
        const slides = res.slides.map(s => ({ ...emptySlide(s.layout || "bullets"), ...s, id: newId() }));
        // Ensure a deck exists
        if (!active) {
          const created = await docsApi.create({
            kind: KIND,
            title: res.title || aiTopic.trim(),
            content: { title: res.title || aiTopic.trim(), template: active?.content?.template || "modern", slides },
          });
          setDocs(ds => [created, ...ds]);
          setActive({ ...created, content: normalize(created.content) });
        } else {
          setActive(a => ({
            ...a,
            title: res.title || aiTopic.trim(),
            content: { ...a.content, title: res.title || aiTopic.trim(), slides },
          }));
        }
        setSelIdx(0);
        setShowAI(false);
      } else if (res?.error) {
        window.alert("AI generation error: " + res.error);
      }
    } catch (e) {
      window.alert("AI generation failed: " + (e?.message || "unknown"));
    } finally {
      setAiBusy(false);
    }
  };

  const doExport = async (kind) => {
    if (!active) return;
    setExportBusy(kind);
    try {
      const payload = { title: active.title || active.content.title, slides: active.content.slides, template: active.content.template };
      const blob = kind === "pptx" ? await slidesApi.exportPptx(payload) : await slidesApi.exportPdf(payload);
      const filename = (active.title || "presentation").replace(/[^\w\-]+/g, "_") + (kind === "pptx" ? ".pptx" : ".pdf");
      downloadBlob(blob, filename);
    } catch (e) {
      window.alert("Export failed: " + (e?.message || "unknown"));
    } finally {
      setExportBusy(null);
    }
  };

  // Empty state
  if (!active) {
    return (
      <div className="h-full flex flex-col bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <TopBar
          title=""
          onTitle={() => {}}
          onNew={createDeck}
          disabled
        />
        <div className="flex-1 flex flex-col items-center justify-center gap-6 p-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
            <PresentationIcon className="w-8 h-8 text-white" />
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-slate-800">Slides — AI Presentation Maker</div>
            <div className="text-sm text-slate-500 mt-1">30+ templates · charts · infographics · export to PPTX/PDF</div>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="slides-create-blank-btn"
              onClick={createDeck}
              className="px-5 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> New Blank Deck
            </button>
            <button
              data-testid="slides-ai-generate-open-btn"
              onClick={() => { setShowAI(true); }}
              className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-sm font-medium hover:opacity-90 flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" /> Generate with AI
            </button>
          </div>
          {docs.length > 0 && (
            <div className="w-full max-w-2xl mt-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Recent decks</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {docs.slice(0, 10).map(d => (
                  <button
                    key={d.id}
                    data-testid={`slides-open-deck-${d.id}`}
                    onClick={() => { setActive({ ...d, content: normalize(d.content) }); setSelIdx(0); }}
                    className="text-left px-4 py-3 rounded-lg bg-white border border-slate-200 hover:border-indigo-400 hover:shadow-sm transition"
                  >
                    <div className="font-medium text-sm text-slate-800 truncate">{d.title || "Untitled"}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{(d.content?.slides?.length || 0)} slides</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        {showAI && (
          <AIModal
            topic={aiTopic} setTopic={setAiTopic}
            count={aiSlideCount} setCount={setAiSlideCount}
            tone={aiTone} setTone={setAiTone}
            audience={aiAudience} setAudience={setAiAudience}
            charts={aiCharts} setCharts={setAiCharts}
            infographics={aiInfographics} setInfographics={setAiInfographics}
            busy={aiBusy} onClose={() => setShowAI(false)} onRun={runAI}
          />
        )}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <TopBar
        title={active.title || ""}
        onTitle={(v) => setActive(a => ({ ...a, title: v, content: { ...a.content, title: v } }))}
        onNew={createDeck}
        onDelete={deleteDeck}
        onAI={() => setShowAI(true)}
        onExport={doExport}
        exportBusy={exportBusy}
        onTemplates={() => setShowTemplates(s => !s)}
        templateName={theme.name}
        onFont={(f) => patchSlide({ font_override: f })}
        currentFont={slide?.font_override || theme.font}
        decks={docs}
        activeDeckId={active.id}
        onSwitchDeck={(id) => {
          const d = docs.find(x => x.id === id);
          if (d) { setActive({ ...d, content: normalize(d.content) }); setSelIdx(0); }
        }}
      />

      <div className="flex-1 min-h-0 flex">
        {/* Left: slide list */}
        <div className="w-56 border-r border-slate-200 bg-white overflow-y-auto p-2 flex flex-col gap-2" data-testid="slides-thumb-list">
          {active.content.slides.map((s, i) => (
            <button
              key={s.id}
              data-testid={`slides-thumb-${i}`}
              onClick={() => setSelIdx(i)}
              className={`relative rounded-md overflow-hidden border-2 transition ${i === selIdx ? "border-indigo-500 shadow" : "border-transparent hover:border-slate-200"}`}
            >
              <div className="absolute top-1 left-1 z-10 text-[10px] font-mono bg-black/50 text-white rounded px-1">{i + 1}</div>
              <div className="pointer-events-none">
                <div style={{ transform: "scale(0.2)", transformOrigin: "top left", width: 960 * 0.2, height: 540 * 0.2 }}>
                  <SlidePreview slide={s} theme={theme} scale={1} />
                </div>
              </div>
            </button>
          ))}
          <button
            data-testid="slides-add-slide-btn"
            onClick={() => addSlide("bullets")}
            className="mt-1 flex items-center justify-center gap-2 py-3 rounded-md border-2 border-dashed border-slate-300 text-slate-500 hover:border-indigo-400 hover:text-indigo-600 text-xs font-medium"
          >
            <Plus className="w-4 h-4" /> Add slide
          </button>
        </div>

        {/* Center: preview stage */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 bg-white text-xs text-slate-500">
            <div className="flex items-center gap-2">
              <button data-testid="slides-prev-btn" onClick={() => setSelIdx(i => Math.max(0, i - 1))} className="p-1 hover:bg-slate-100 rounded"><ChevronLeft className="w-4 h-4" /></button>
              <span data-testid="slides-current-index">Slide {selIdx + 1} / {active.content.slides.length}</span>
              <button data-testid="slides-next-btn" onClick={() => setSelIdx(i => Math.min(active.content.slides.length - 1, i + 1))} className="p-1 hover:bg-slate-100 rounded"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="slides-duplicate-btn" onClick={duplicateSlide} title="Duplicate" className="p-1.5 hover:bg-slate-100 rounded"><Copy className="w-4 h-4" /></button>
              <button data-testid="slides-delete-slide-btn" onClick={removeSlide} title="Delete" className="p-1.5 hover:bg-red-50 rounded text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
          <div ref={stageRef} className="flex-1 min-h-0 overflow-auto flex items-center justify-center p-6 bg-slate-100" data-testid="slides-stage">
            {slide && (
              <SlidePreview
                slide={slide}
                theme={theme}
                scale={stageScale}
                editable
                onEdit={patchSlide}
              />
            )}
          </div>
        </div>

        {/* Right: properties */}
        <div className="w-80 border-l border-slate-200 bg-white overflow-y-auto p-4" data-testid="slides-properties">
          <Section label="Layout">
            <div className="grid grid-cols-3 gap-1.5">
              {LAYOUTS.map(l => (
                <button
                  key={l.id}
                  data-testid={`slides-layout-${l.id}`}
                  onClick={() => patchSlide({ layout: l.id })}
                  className={`text-xs px-2 py-2 rounded border transition ${slide?.layout === l.id ? "bg-indigo-500 text-white border-indigo-500" : "bg-white border-slate-200 hover:border-indigo-300 text-slate-700"}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </Section>

          <SlideEditor slide={slide} onChange={patchSlide} />

          <Section label="Slide Notes">
            <textarea
              data-testid="slides-notes-input"
              value={slide?.notes || ""}
              onChange={(e) => patchSlide({ notes: e.target.value })}
              rows={3}
              className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none resize-y"
              placeholder="Speaker notes…"
            />
          </Section>
        </div>
      </div>

      {/* Template picker overlay */}
      {showTemplates && (
        <TemplatePicker
          currentId={active.content.template}
          onPick={(id) => { patchDeck({ template: id }); setShowTemplates(false); }}
          onClose={() => setShowTemplates(false)}
        />
      )}

      {showAI && (
        <AIModal
          topic={aiTopic} setTopic={setAiTopic}
          count={aiSlideCount} setCount={setAiSlideCount}
          tone={aiTone} setTone={setAiTone}
          audience={aiAudience} setAudience={setAiAudience}
          charts={aiCharts} setCharts={setAiCharts}
          infographics={aiInfographics} setInfographics={setAiInfographics}
          busy={aiBusy} onClose={() => setShowAI(false)} onRun={runAI}
        />
      )}
    </div>
  );
}

function TopBar({ title, onTitle, onNew, onDelete, onAI, onExport, exportBusy, onTemplates, templateName, onFont, currentFont, disabled, decks, activeDeckId, onSwitchDeck }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200 bg-white">
      <div className="flex items-center gap-1.5 text-slate-500">
        <PresentationIcon className="w-4 h-4" />
        <span className="text-xs font-medium">Slides</span>
      </div>
      <div className="h-4 w-px bg-slate-200 mx-1" />
      {!disabled && decks && decks.length > 1 && (
        <select
          data-testid="slides-deck-switcher"
          value={activeDeckId || ""}
          onChange={(e) => onSwitchDeck?.(e.target.value)}
          className="text-xs bg-slate-50 hover:bg-slate-100 rounded px-2 py-1 border border-slate-200 outline-none max-w-[180px]"
          title="Switch deck"
        >
          {decks.map(d => (
            <option key={d.id} value={d.id}>{d.title || "Untitled"}</option>
          ))}
        </select>
      )}
      <input
        data-testid="slides-title-input"
        value={title}
        onChange={(e) => onTitle(e.target.value)}
        placeholder="Presentation title"
        disabled={disabled}
        className="flex-1 max-w-md text-sm px-2 py-1 rounded border border-transparent hover:border-slate-200 focus:border-indigo-400 outline-none bg-transparent"
      />
      <div className="flex-1" />
      {!disabled && (
        <>
          <button data-testid="slides-templates-btn" onClick={onTemplates} className="text-xs px-2.5 py-1.5 rounded hover:bg-slate-100 flex items-center gap-1.5 text-slate-700">
            <LayoutTemplate className="w-3.5 h-3.5" />
            <span className="hidden md:inline">{templateName || "Templates"}</span>
          </button>
          <div className="flex items-center gap-1">
            <Type className="w-3.5 h-3.5 text-slate-400" />
            <select
              data-testid="slides-font-select"
              value={currentFont || "Inter"}
              onChange={(e) => onFont(e.target.value)}
              className="text-xs bg-transparent hover:bg-slate-100 rounded px-1.5 py-1 border border-transparent hover:border-slate-200 outline-none"
            >
              {FONTS.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
          <div className="h-4 w-px bg-slate-200 mx-1" />
          <button
            data-testid="slides-ai-generate-btn"
            onClick={onAI}
            className="text-xs px-3 py-1.5 rounded bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium hover:opacity-90 flex items-center gap-1.5"
          >
            <Sparkles className="w-3.5 h-3.5" /> AI Generate
          </button>
          <button
            data-testid="slides-export-pptx-btn"
            onClick={() => onExport("pptx")}
            disabled={exportBusy === "pptx"}
            className="text-xs px-2.5 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 flex items-center gap-1.5 disabled:opacity-60"
          >
            {exportBusy === "pptx" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            PPTX
          </button>
          <button
            data-testid="slides-export-pdf-btn"
            onClick={() => onExport("pdf")}
            disabled={exportBusy === "pdf"}
            className="text-xs px-2.5 py-1.5 rounded border border-slate-300 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-60 text-slate-700"
          >
            {exportBusy === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            PDF
          </button>
          <div className="h-4 w-px bg-slate-200 mx-1" />
        </>
      )}
      <button data-testid="slides-new-deck-btn" onClick={onNew} title="New deck" className="p-1.5 rounded hover:bg-slate-100 text-slate-700">
        <Plus className="w-4 h-4" />
      </button>
      {onDelete && (
        <button data-testid="slides-delete-deck-btn" onClick={onDelete} title="Delete deck" className="p-1.5 rounded hover:bg-red-50 text-red-500">
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="mb-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function TextInput(props) {
  return (
    <input
      {...props}
      className={`w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none ${props.className || ""}`}
    />
  );
}

function ArrayEditor({ items, onChange, placeholder, testIdPrefix }) {
  const list = items || [];
  const setAt = (i, v) => onChange(list.map((x, j) => j === i ? v : x));
  const removeAt = (i) => onChange(list.filter((_, j) => j !== i));
  const add = () => onChange([...list, ""]);
  return (
    <div className="space-y-1.5">
      {list.map((v, i) => (
        <div key={i} className="flex items-center gap-1">
          <TextInput data-testid={`${testIdPrefix}-${i}`} value={v} onChange={(e) => setAt(i, e.target.value)} placeholder={placeholder} />
          <button onClick={() => removeAt(i)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <button data-testid={`${testIdPrefix}-add`} onClick={add} className="text-xs flex items-center gap-1 text-indigo-600 hover:text-indigo-700"><Plus className="w-3 h-3" /> Add item</button>
    </div>
  );
}

function SlideEditor({ slide, onChange }) {
  if (!slide) return null;
  const l = slide.layout;
  return (
    <>
      {(l !== "title" && l !== "closing") ? (
        <>
          <Section label="Heading">
            <TextInput data-testid="slides-heading-input" value={slide.heading || ""} onChange={(e) => onChange({ heading: e.target.value })} placeholder="Slide heading" />
          </Section>
          <Section label="Subheading">
            <TextInput data-testid="slides-subheading-input" value={slide.subheading || ""} onChange={(e) => onChange({ subheading: e.target.value })} placeholder="Optional subheading" />
          </Section>
        </>
      ) : (
        <>
          <Section label={l === "title" ? "Title" : "Closing text"}>
            <TextInput data-testid="slides-title-heading" value={slide.heading || ""} onChange={(e) => onChange({ heading: e.target.value })} />
          </Section>
          <Section label="Subtitle">
            <TextInput data-testid="slides-title-subheading" value={slide.subheading || ""} onChange={(e) => onChange({ subheading: e.target.value })} />
          </Section>
        </>
      )}

      {(l === "bullets" || l === "team") && (
        <Section label={l === "team" ? "Team members" : "Bullets"}>
          <ArrayEditor items={slide.bullets || []} onChange={(v) => onChange({ bullets: v })} placeholder={l === "team" ? "Member name" : "Bullet point"} testIdPrefix="slides-bullet" />
        </Section>
      )}

      {(l === "stat" || l === "infographic") && (
        <Section label="Stats">
          <div className="space-y-2">
            {(slide.stats || []).map((s, i) => (
              <div key={i} className="grid grid-cols-3 gap-1">
                <TextInput data-testid={`slides-stat-value-${i}`} value={s.value || ""} onChange={(e) => onChange({ stats: (slide.stats || []).map((x, j) => j === i ? { ...x, value: e.target.value } : x) })} placeholder="42" />
                <TextInput data-testid={`slides-stat-unit-${i}`} value={s.unit || ""} onChange={(e) => onChange({ stats: (slide.stats || []).map((x, j) => j === i ? { ...x, unit: e.target.value } : x) })} placeholder="%" />
                <TextInput data-testid={`slides-stat-label-${i}`} value={s.label || ""} onChange={(e) => onChange({ stats: (slide.stats || []).map((x, j) => j === i ? { ...x, label: e.target.value } : x) })} placeholder="Growth" />
              </div>
            ))}
            <button data-testid="slides-stat-add" onClick={() => onChange({ stats: [...(slide.stats || []), { value: "", unit: "", label: "" }] })} className="text-xs flex items-center gap-1 text-indigo-600 hover:text-indigo-700"><Plus className="w-3 h-3" /> Add stat</button>
          </div>
        </Section>
      )}

      {l === "quote" && (
        <>
          <Section label="Quote">
            <textarea data-testid="slides-quote-input" value={slide.quote || ""} onChange={(e) => onChange({ quote: e.target.value })} rows={3}
              className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none resize-y" />
          </Section>
          <Section label="Author">
            <TextInput data-testid="slides-quote-author" value={slide.author || ""} onChange={(e) => onChange({ author: e.target.value })} placeholder="Author name" />
          </Section>
        </>
      )}

      {(l === "two_column" || l === "comparison") && (
        <Section label="Columns">
          <div className="space-y-2">
            {(slide.columns || []).slice(0, 2).map((c, i) => (
              <div key={i} className="p-2 rounded border border-slate-200 space-y-1.5">
                <TextInput data-testid={`slides-col-heading-${i}`} value={c.heading || ""} onChange={(e) => onChange({ columns: (slide.columns || []).map((x, j) => j === i ? { ...x, heading: e.target.value } : x) })} placeholder="Column heading" />
                <textarea data-testid={`slides-col-body-${i}`} value={c.body || ""} onChange={(e) => onChange({ columns: (slide.columns || []).map((x, j) => j === i ? { ...x, body: e.target.value } : x) })} rows={2}
                  className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none resize-y" placeholder="Body text" />
              </div>
            ))}
          </div>
        </Section>
      )}

      {l === "chart" && (
        <Section label="Chart">
          <div className="space-y-2">
            <select
              data-testid="slides-chart-kind"
              value={slide.chart?.kind || "bar"}
              onChange={(e) => onChange({ chart: { ...(slide.chart || {}), kind: e.target.value } })}
              className="w-full text-sm px-2 py-1.5 rounded border border-slate-200 focus:border-indigo-400 outline-none"
            >
              <option value="bar">Bar</option>
              <option value="line">Line</option>
              <option value="area">Area</option>
              <option value="pie">Pie</option>
            </select>
            {(slide.chart?.data || []).map((d, i) => (
              <div key={i} className="grid grid-cols-2 gap-1">
                <TextInput data-testid={`slides-chart-name-${i}`} value={d.name || ""} onChange={(e) => onChange({ chart: { ...(slide.chart || {}), data: (slide.chart?.data || []).map((x, j) => j === i ? { ...x, name: e.target.value } : x) } })} placeholder="Label" />
                <TextInput data-testid={`slides-chart-value-${i}`} type="number" value={d.value ?? ""} onChange={(e) => onChange({ chart: { ...(slide.chart || {}), data: (slide.chart?.data || []).map((x, j) => j === i ? { ...x, value: Number(e.target.value) } : x) } })} placeholder="Value" />
              </div>
            ))}
            <button
              data-testid="slides-chart-add-row"
              onClick={() => onChange({ chart: { kind: slide.chart?.kind || "bar", data: [...(slide.chart?.data || []), { name: "", value: 0 }] } })}
              className="text-xs flex items-center gap-1 text-indigo-600 hover:text-indigo-700"
            ><Plus className="w-3 h-3" /> Add row</button>
          </div>
        </Section>
      )}

      {l === "timeline" && (
        <Section label="Timeline">
          <div className="space-y-2">
            {(slide.timeline || []).map((t, i) => (
              <div key={i} className="p-2 rounded border border-slate-200 space-y-1">
                <TextInput data-testid={`slides-timeline-date-${i}`} value={t.date || ""} onChange={(e) => onChange({ timeline: (slide.timeline || []).map((x, j) => j === i ? { ...x, date: e.target.value } : x) })} placeholder="Date / Q1 2024" />
                <TextInput data-testid={`slides-timeline-title-${i}`} value={t.title || ""} onChange={(e) => onChange({ timeline: (slide.timeline || []).map((x, j) => j === i ? { ...x, title: e.target.value } : x) })} placeholder="Milestone" />
                <TextInput data-testid={`slides-timeline-desc-${i}`} value={t.desc || ""} onChange={(e) => onChange({ timeline: (slide.timeline || []).map((x, j) => j === i ? { ...x, desc: e.target.value } : x) })} placeholder="Description" />
              </div>
            ))}
            <button
              data-testid="slides-timeline-add"
              onClick={() => onChange({ timeline: [...(slide.timeline || []), { date: "", title: "", desc: "" }] })}
              className="text-xs flex items-center gap-1 text-indigo-600 hover:text-indigo-700"
            ><Plus className="w-3 h-3" /> Add milestone</button>
          </div>
        </Section>
      )}
    </>
  );
}

function TemplatePicker({ currentId, onPick, onClose }) {
  return (
    <div className="absolute inset-0 z-40 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-5xl h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        data-testid="slides-templates-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-indigo-500" />
            <div className="font-semibold text-slate-800">Choose a Template ({TEMPLATES.length})</div>
          </div>
          <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-800">Close</button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {TEMPLATES.map(t => (
              <button
                key={t.id}
                data-testid={`slides-template-${t.id}`}
                onClick={() => onPick(t.id)}
                className={`text-left rounded-xl overflow-hidden border-2 transition ${currentId === t.id ? "border-indigo-500 ring-2 ring-indigo-200" : "border-slate-200 hover:border-indigo-300"}`}
              >
                <div className="h-32 relative" style={{ background: t.bg }}>
                  <div className="absolute inset-0 p-3 flex flex-col justify-between" style={{ fontFamily: `'${t.font}', system-ui, sans-serif` }}>
                    <div style={{ color: t.fg }} className="text-sm font-bold leading-tight">Modern Deck</div>
                    <div className="flex gap-1">
                      <div className="h-1 w-6 rounded-full" style={{ background: t.accent }} />
                      <div className="h-1 w-3 rounded-full" style={{ background: t.fg, opacity: 0.3 }} />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: t.accent }} />
                </div>
                <div className="px-3 py-2 bg-white">
                  <div className="text-xs font-medium text-slate-800 truncate">{t.name}</div>
                  <div className="text-[10px] text-slate-500">{t.font}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AIModal({ topic, setTopic, count, setCount, tone, setTone, audience, setAudience, charts, setCharts, infographics, setInfographics, busy, onClose, onRun }) {
  return (
    <div className="absolute inset-0 z-40 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="slides-ai-modal">
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
          <div className="flex items-center gap-2 font-semibold"><Sparkles className="w-5 h-5" /> Generate with AI</div>
          <div className="text-xs opacity-90 mt-0.5">Let Novo draft an entire deck from a topic.</div>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Topic *</label>
            <input
              data-testid="slides-ai-topic-input"
              value={topic} onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. Q4 sales strategy for enterprise clients"
              className="w-full text-sm px-3 py-2 rounded border border-slate-200 focus:border-indigo-400 outline-none"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Slides</label>
              <input
                data-testid="slides-ai-count-input"
                type="number" min={3} max={30} value={count} onChange={(e) => setCount(Number(e.target.value))}
                className="w-full text-sm px-3 py-2 rounded border border-slate-200 focus:border-indigo-400 outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700 block mb-1">Tone</label>
              <select
                data-testid="slides-ai-tone-select"
                value={tone} onChange={(e) => setTone(e.target.value)}
                className="w-full text-sm px-3 py-2 rounded border border-slate-200 focus:border-indigo-400 outline-none bg-white"
              >
                <option value="professional">Professional</option>
                <option value="friendly">Friendly</option>
                <option value="persuasive">Persuasive</option>
                <option value="playful">Playful</option>
                <option value="academic">Academic</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-700 block mb-1">Audience (optional)</label>
            <input
              data-testid="slides-ai-audience-input"
              value={audience} onChange={(e) => setAudience(e.target.value)}
              placeholder="e.g. C-level execs, investors, engineers"
              className="w-full text-sm px-3 py-2 rounded border border-slate-200 focus:border-indigo-400 outline-none"
            />
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input data-testid="slides-ai-charts-checkbox" type="checkbox" checked={charts} onChange={(e) => setCharts(e.target.checked)} />
              Include charts
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input data-testid="slides-ai-infographics-checkbox" type="checkbox" checked={infographics} onChange={(e) => setInfographics(e.target.checked)} />
              Include infographics
            </label>
          </div>
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button data-testid="slides-ai-cancel-btn" onClick={onClose} className="text-sm px-4 py-2 rounded hover:bg-slate-200 text-slate-700">Cancel</button>
          <button
            data-testid="slides-ai-run-btn"
            onClick={onRun}
            disabled={busy || !topic.trim()}
            className="text-sm px-4 py-2 rounded bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium hover:opacity-90 flex items-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Generate
          </button>
        </div>
      </div>
    </div>
  );
}
